import * as React from "react";
import {
    ContentFilter,
    ContentTable,
    ProcessTypeOptions,
    CreatedbyOptions,
    FirmTypeOptions,
} from "../Resource/Content";
import { CompanyWizardStore } from "../Stores/Variable";
import { BaseComponent } from "../../../../../react-core/Html/BaseComponent";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { DatePicker } from "../../../../../react-core/Datepicker/DatePicker";
import { addYears } from "../../../../../react-core/utils/date";
import { ContentDatePicker, ACCEPT_DATE_FORMATS } from "../../../../Shared/Entities/Constants/DateConstants";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { AllowFourSpecial, AllowOneSpecial, NoSpecial, Numeric } from "../../../../../react-core/Html/Validation/RegExPatterns";
import { errorMsg } from "../../../Network/Resource/Content";
import { SearchInput } from "../../../../Shared/SearchInput/SearchInput";
import { NetworkHistoryClient } from "../../../../../Wrap/Api/Network/NetworkHistory";
import { NetworkHistoryModel } from "../../../../../Wrap/Models/Models/Network/NetworkHistoryModel";
import { NetworkType } from "../../../../../Wrap/Constants/Enums/Network/NetworkType";

interface ICompanyWizardFilterProps {
    firmID?: string;
    processType: any;
    firmType: any;
    firmName: string;
    reference: string;
    postcode: string;
    createdBy: any;
    dateStartedFrom: Date;
    dateStartedTo: Date;
    lastUpdatedFrom: Date;
    lastUpdatedTo: Date;
    handleFilterChange: any;
    onSearch: any;
    isSetup?: boolean;
    displayNetworkSearch: boolean;
}

interface ICompanyWizardFilterState {
    network: NetworkHistoryModel;
}

export class CompanyWizardFilter extends FormComponent<ICompanyWizardFilterProps, ICompanyWizardFilterState> {
    constructor(props: ICompanyWizardFilterProps) {
        super(props);
        this.state = { network: null };
    }

    public render(): JSX.Element {
        return (
            <>
                {this.renderFilters()}
                {!this.props.isSetup && this.renderDateRanges()}
            </>
        );
    }
    private renderFilters(): JSX.Element {
        const {
            processType,
            firmType,
            firmName,
            reference,
            postcode,
            createdBy,
            handleFilterChange,
            firmID,
            displayNetworkSearch,
        } = this.props;
        const networkClient = new NetworkHistoryClient();
        const { network } = this.state;
        return (
            <>
                <h1 className="page-heading">{ContentFilter.title}</h1>
                <div className="row narrow-gutter">
                    <div className="col-12 col-sm-4">
                        {this.props.isSetup ? (
                            <TextBoxEditor
                                label="Firm ID"
                                value={firmID}
                                onChange={(v) => handleFilterChange({ firmID: v })}
                            />
                        ) : (
                            <SelectEditor
                                label={ContentFilter.processType}
                                selected={processType}
                                options={ProcessTypeOptions.map((item) => item.key)}
                                getText={(key) => CommonHelper.getOptionsTxt(key, ProcessTypeOptions)}
                                onChange={(v) => handleFilterChange({ processType: v })}
                            />
                        )}
                    </div>
                    <div className="col-12 col-sm-4">
                        <SelectEditor
                            label={ContentFilter.firmType}
                            selected={firmType}
                            options={FirmTypeOptions.map((item) => item.key)}
                            getText={(key) => CommonHelper.getOptionsTxt(key, FirmTypeOptions)}
                            onChange={(v) => handleFilterChange({ firmType: v })}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <TextBoxEditor
                            value={firmName}
                            label={ContentFilter.firmName}
                            maxLength={50}
                            regex={{ value: AllowFourSpecial, errorMessage: "There are only 4 special characters allowed." }}
                            onChange={(v) => handleFilterChange({ firmName: v })}
                        />
                    </div>
                    {this.props.displayNetworkSearch && (
                        <div className="col-12 col-sm-4">
                            <SearchInput
                                apiClient={(v) => {
                                    return networkClient.get(null, NetworkType.Unknown, v, null, null, true, 20, 1);
                                }}
                                onClick={(v: NetworkHistoryModel) => {
                                    this.setState({ network: v });
                                    handleFilterChange({ network: v.Network });
                                }}
                                resultKey="networks"
                                getText={(v: NetworkHistoryModel) => v.NetworkName}
                                label={"Network"}
                                value={network?.NetworkName}
                            />
                        </div>
                    )}
                    <div className="col-12 col-sm-4">
                        <TextBoxEditor
                            value={reference}
                            label={ContentFilter.reference}
                            maxLength={8}
                            regex={{ errorMessage: "Must be numeric values only", value: Numeric }}
                            onChange={(v) => handleFilterChange({ reference: v })}
                        />
                    </div>
                    <div className="col-12 col-sm-4">
                        <TextBoxEditor
                            value={postcode}
                            label={ContentFilter.postcode}
                            regex={{ errorMessage: errorMsg.noSpecial, value: NoSpecial }}
                            onChange={(v) => handleFilterChange({ postcode: v })}
                        />
                    </div>
                    {!this.props.isSetup && (
                        <div className="col-12 col-sm-4">
                            <SelectEditor
                                label={ContentFilter.createdBy}
                                selected={createdBy}
                                options={CreatedbyOptions.map((item) => item.key)}
                                getText={(key) => CommonHelper.getOptionsTxt(key, CreatedbyOptions)}
                                onChange={(v) => handleFilterChange({ createdBy: v })}
                            />
                        </div>
                    )}
                    {this.props.isSetup && (
                        <div className="col-auto">
                            <div className="form-group">
                                <div className="controls">
                                    <button
                                        className="btn secondary label-offset"
                                        onClick={() => {
                                            if (this.isValid()) {
                                                this.props.onSearch();
                                            }
                                        }}
                                    >
                                        {ContentFilter.search}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    }

    private renderDateRanges(): JSX.Element {
        const {
            dateStartedFrom,
            dateStartedTo,
            lastUpdatedFrom,
            lastUpdatedTo,
            handleFilterChange,
            onSearch,
        } = this.props;
        return (
            <>
                <div className="row narrow-gutter">
                    <div className="col-xs-6 col-sm-fluid">
                        <h3>{ContentFilter.dateStarted}</h3>
                        <div className="row narrow-gutter">
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.from}
                                    selected={dateStartedFrom}
                                    atLeast={
                                        dateStartedFrom
                                            ? {
                                                  value: ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        dateStartedFrom
                                            ? {
                                                  value: dateStartedTo ? dateStartedTo : new Date(),
                                                  errorMessage: ContentDatePicker.CannotBeInTheFuture,
                                              }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) => handleFilterChange({ dateStartedFrom: d })}
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.to}
                                    selected={dateStartedTo}
                                    atLeast={
                                        dateStartedTo
                                            ? {
                                                  value: dateStartedFrom
                                                      ? dateStartedFrom
                                                      : ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        dateStartedTo
                                            ? { value: new Date(), errorMessage: ContentDatePicker.CannotBeInTheFuture }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) => handleFilterChange({ dateStartedTo: d })}
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-xs-6 col-sm-fluid">
                        <h3>{ContentFilter.lastUpdated}</h3>
                        <div className="row narrow-gutter">
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.from}
                                    selected={lastUpdatedFrom}
                                    atLeast={
                                        lastUpdatedFrom
                                            ? {
                                                  value: ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        lastUpdatedFrom
                                            ? {
                                                  value: lastUpdatedTo ? lastUpdatedTo : new Date(),
                                                  errorMessage: ContentDatePicker.CannotBeInTheFuture,
                                              }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) => handleFilterChange({ lastUpdatedFrom: d })}
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                            <div className="col-fluid datepicker-container">
                                <DatePicker
                                    label={ContentFilter.to}
                                    selected={lastUpdatedTo}
                                    atLeast={
                                        lastUpdatedTo
                                            ? {
                                                  value: lastUpdatedFrom
                                                      ? lastUpdatedFrom
                                                      : ContentDatePicker.DefaultMinDate,
                                                  errorMessage: ContentDatePicker.CannotBeInThePast,
                                              }
                                            : null
                                    }
                                    atMost={
                                        lastUpdatedTo
                                            ? { value: new Date(), errorMessage: ContentDatePicker.CannotBeInTheFuture }
                                            : null
                                    }
                                    showYearDropdown={true}
                                    onChange={(d) => handleFilterChange({ lastUpdatedTo: d })}
                                    todayButton={ContentDatePicker.Today}
                                    isClearable={true}
                                    dateFormat={ContentDatePicker.DateFormat}
                                    acceptDateFormats={ACCEPT_DATE_FORMATS}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto col-bottom">
                        <div className="form-group">
                            <div className="controls">
                                <button
                                    className="btn secondary mb-4"
                                    onClick={() => {
                                        if (this.isValid()) {
                                            onSearch();
                                        }
                                    }}
                                >
                                    {ContentFilter.search}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}
